import { DestroyRef, Signal, inject, signal } from '@angular/core';

/** Los peldaños de la escala de `design-system/_breakpoints.scss`. */
export type DxcBreakpoint = 'lg' | 'md' | 'sm';

/**
 * El token que la capa de tokens genera de cada peldaño. `lg` conserva su
 * nombre histórico porque `layout.component.ts` ya lo leía para el cajón.
 */
const TOKEN: Record<DxcBreakpoint, string> = {
    lg: '--dxc-shell-drawer-bp',
    md: '--dxc-bp-md',
    sm: '--dxc-bp-sm',
};

/**
 * La consulta `(max-width: …)` de un peldaño, leída de la capa de tokens. CSS
 * no admite `var()` dentro de `@media`, así que la escala se declara una vez en
 * Sass, `_tokens.scss` la repite como `--dxc-bp-*` y JS la lee de ahí: escribir
 * 768 aquí sería la segunda copia que un día diría otra cosa.
 *
 * Sin el token —una hoja que no cargó— devuelve `not all`: nunca casa, así que
 * lo que dependa de ella se queda en su forma de escritorio, que es la segura.
 */
export function dxcBreakpointQuery(name: DxcBreakpoint): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(TOKEN[name]).trim();
    return value ? `(max-width: ${value})` : 'not all';
}

/**
 * `true` mientras la ventana está por debajo del peldaño (`max-width`, igual que
 * `dxc-below()` en Sass). Para lo que CSS no puede decidir solo: montar un
 * elemento que en escritorio no existe —el botón «Filtros» de `dxc-table`, el
 * menú «Más» del encabezado— sin dejarle una regla base en la hoja.
 *
 * Se llama en contexto de inyección (un inicializador de campo); se da de baja
 * sola al destruirse el componente.
 *
 * @example
 * readonly compact = dxcBelow('md');
 * // @if (compact()) { <button class="dxc-filterbar__toggle">…</button> }
 */
export function dxcBelow(name: DxcBreakpoint): Signal<boolean> {
    const query = window.matchMedia(dxcBreakpointQuery(name));
    const state = signal(query.matches);
    const onChange = (event: MediaQueryListEvent) => state.set(event.matches);
    query.addEventListener('change', onChange);
    inject(DestroyRef).onDestroy(() => query.removeEventListener('change', onChange));
    return state.asReadonly();
}
